import React, {useContext, useEffect, useState} from 'react';
import {useAuth0} from "@auth0/auth0-react";
import Button, {StyleType} from "patient-ping-remedy/packages/button";
import {useAuthStore} from "@/store/auth_store";
import CarecoApi from '@/api/careco-api';
import {Attestation, LoginError, LoginErrorType, User} from '@/api/dto/dto';
import Modal from "patient-ping-remedy/packages/modal";
import {css} from "@emotion/css";
import {SmallTypography} from "patient-ping-remedy/packages/typography";
import {CarecoApiContext} from "@/app-context/careco-api-context";
import ErrorPage from "@/components/pages/error_pages/ErrorPage";
import {router} from "@/router";
import {useMutation} from "@tanstack/react-query";
import EncounterApi from "@/api/encounter-api";
import WebSocketApi from "@/api/websocket-api";
import {log} from "@/helpers/logger";
import {MixpanelEventType, MixpanelLoginEvent} from "@/api/dto/mixpanel";
import {useMixpanel} from "@/hooks/useMixpanel";

type Props = {
  login?: boolean;
}

// After login, user is redirected here to check attestation status (with login prop)
// When logged in, user might be redirected here to check attestation status (without login prop)
const ConsentPage = (props: Props) => {
  const {user, isAuthenticated, isLoading, logout, getAccessTokenSilently, loginWithRedirect} = useAuth0();
  const {
    attestationCheck,
    setAttestationCheck,
    phoneVerificationCheck,
    setPhoneVerificationCheck,
    returnToRoute
  } = useAuthStore();
  const [showConsentPopup, setShowConsentPopup] = useState(false);
  const [attestation, setAttestation] = useState<Attestation>();
  const [loadError, setLoadError] = useState(false);
  const [userSsoData, setUserSsoData] = useState<{
    isSso: boolean;
    ssoConnection: string | null;
  }>({isSso: false, ssoConnection: null});
  const {carecoApi, updateState} = useContext(CarecoApiContext);
  const {mixpanelEventProperties} = useMixpanel(returnToRoute);
  const postAttestationMutation = useMutation({
    mutationFn: async (document_version: string) => carecoApi?.postAttestation(document_version),
    onSuccess: () => {
      router.navigate(returnToRoute ? returnToRoute : '/clients');
    },
  });

  const checkLogin = async () => {
    const carecoApi = new CarecoApi(getAccessTokenSilently);
    const encounterApi = new EncounterApi(getAccessTokenSilently);
    const webSocketApi = new WebSocketApi();
    updateState({carecoApi, encounterApi, webSocketApi});

    // Fetch user data for SSO information
    let ssoData = {isSso: false, ssoConnection: null as string | null};
    if (user?.sub) {
      try {
        const userData: User = await carecoApi.getUser(user.sub);
        ssoData = {
          isSso: Boolean(userData.sso_connection),
          ssoConnection: userData.sso_connection || null
        };
        setUserSsoData(ssoData);
      } catch (error) {
        log('error', 'Failed to fetch user data:', error);
      }
    }

    if (!attestationCheck || !phoneVerificationCheck) {
      (props.login
        ? carecoApi.login()
        : carecoApi.getLoginPrompts()
      )
        .then((response) => {
          if (!response.phone_verified) {
            logout({
              logoutParams: {
                returnTo: window.location.origin + '/failed_phone_verification',
              },
            });
          } else if (response && response.attestation) {
            setPhoneVerificationCheck(true);

            setAttestation(response.attestation);
            setShowConsentPopup(true);
          } else {
            setPhoneVerificationCheck(true);

            // no attestation needed, set sessionStorage
            setAttestationCheck(true);
            sessionStorage.setItem('access_check', btoa(JSON.stringify({
              phone_verification_check: true,
              attestation_check: true,
              timestamp: new Date().getTime()
            })));
            setAttestation(null!);
            setShowConsentPopup(false);

            const loginEvent: MixpanelLoginEvent = {
              eventName: MixpanelEventType.BIH_USER_LOGGED_IN,
              ...mixpanelEventProperties!,
              isSso: ssoData.isSso,
              ssoConnection: ssoData.ssoConnection,
            };
            carecoApi.postMixpanelEvent(loginEvent);

            router.navigate(returnToRoute ? returnToRoute : '/clients');
          }
        })
        .catch((error) => {
          log('error', error);
          const loginError = error?.response?.data as LoginError;
          switch (loginError?.type) {
          case LoginErrorType.USER_DISABLED:
            logout({
              logoutParams: {
                returnTo: window.location.origin + '/unauthorized',
              },
            });
            break;
          case LoginErrorType.SSO_ACCESS_DENIED:
            sessionStorage.setItem('sso_access_denied', JSON.stringify(error.response.data));
            logout({
              logoutParams: {
                returnTo: window.location.origin + '/sso_access_denied',
              },
            });
            break;
          case LoginErrorType.USER_DELETED:
            sessionStorage.setItem('user_deleted', JSON.stringify(error.response.data));
            logout({
              logoutParams: {
                returnTo: window.location.origin + '/unauthorized',
              },
            });
            break;
          default:
            setLoadError(true);
          }
        });
    }
  };

  useEffect(() => {
    if (isLoading) return;

    const params = new URLSearchParams(window.location.search);

    if (!isAuthenticated) {
      sessionStorage.removeItem('access_check');

      if (params.get('error_description') === "user is blocked") {
        router.navigate('/unauthorized');
        return;
      }

      loginWithRedirect();
      return;
    }

    checkLogin();
  }, [isLoading, isAuthenticated, getAccessTokenSilently, attestationCheck]);

  const handleCancelClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    sessionStorage.removeItem('access_check');
    logout({
      logoutParams: {
        returnTo: window.location.origin + '/complete_attestation',
      },
    });
  };

  const handleAgreeClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    // agreed to attestation, set sessionStorage
    setAttestationCheck(true);
    setPhoneVerificationCheck(true);
    sessionStorage.setItem('access_check', btoa(JSON.stringify({
      phone_verification_check: true,
      attestation_check: true,
      timestamp: new Date().getTime()
    })));
    setShowConsentPopup(false);

    const carecoApi = new CarecoApi(getAccessTokenSilently);
    const loginEvent: MixpanelLoginEvent = {
      eventName: MixpanelEventType.BIH_USER_LOGGED_IN,
      ...mixpanelEventProperties!,
      isSso: userSsoData.isSso,
      ssoConnection: userSsoData.ssoConnection,
    };
    carecoApi.postMixpanelEvent(loginEvent);

    if (attestation?.document_version) postAttestationMutation.mutate(attestation?.document_version);
  };

  if (loadError) {
    return <ErrorPage/>;
  }

  return (
    <>
      <Modal
        id={"consent-modal"}
        headerText={"Consent"}
        isOpen={showConsentPopup}
        buttons={[
          <Button key={'cancelButton'} onClick={handleCancelClick} styleType={StyleType.TERTIARY}>Cancel</Button>,
          <Button key={'agreeButton'} onClick={handleAgreeClick}>I Agree</Button>
        ]}
        className={css({width: '70rem'})}
      >
        <SmallTypography>
          {attestation?.text}
        </SmallTypography>
      </Modal>
    </>
  );
};

export default ConsentPage;
